/**
 * Initiative Panel Component
 *
 * Streamlined initiative tracker:
 * - Party members auto-populate from character store
 * - Click to roll initiative (d20 + DEX mod)
 * - Quick add for mobs
 * - DM can manually adjust any value
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useInitiativeStore, type InitiativeEntry } from '../stores/initiativeStore';
import { useCharacterStore } from '../stores/characterStore';
import { useSessionStore } from '../stores/sessionStore';
import { useTokenStore } from '../stores/tokenStore';
import { useDiceStore } from '../stores/diceStore';
import { useChatStore } from '../stores/chatStore';
import { useCombatStore } from '../stores/combatStore';
import { getModifier } from '@machina/core';
import { Icon, type IconName } from './Icon';
import { DiceButton } from './DiceButton';
import { getBadgeById } from './badges';

// =============================================================================
// ORNATE HEADER COMPONENT
// =============================================================================

interface OrnateHeaderProps {
  isInCombat: boolean;
  round: number;
  isFloating: boolean;
  onStartDrag: (e: React.MouseEvent) => void;
  onDock: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

function OrnateHeader({
  isInCombat,
  round,
  isFloating,
  onStartDrag,
  onDock,
  isExpanded,
  onToggleExpand,
  isCollapsed,
  onToggleCollapse,
}: OrnateHeaderProps) {
  return (
    <div
      onMouseDown={onStartDrag}
      style={{
        position: 'relative',
        padding: '8px 12px',
        background: 'linear-gradient(180deg, rgba(45, 45, 55, 0.8) 0%, rgba(30, 30, 38, 0.9) 100%)',
        borderBottom: '1px solid var(--color-border)',
        cursor: 'grab',
        userSelect: 'none',
      }}
    >
      {/* Decorative corner flourishes */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}>
        {/* Top left corner */}
        <div style={{
          position: 'absolute',
          top: 2,
          left: 4,
          fontSize: '10px',
          color: 'var(--color-primary)',
          opacity: 0.6,
        }}>◆</div>
        {/* Top right corner */}
        <div style={{
          position: 'absolute',
          top: 2,
          right: 4,
          fontSize: '10px',
          color: 'var(--color-primary)',
          opacity: 0.6,
        }}>◆</div>
        {/* Decorative line */}
        <div style={{
          position: 'absolute',
          bottom: -1,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, var(--color-primary), transparent)',
          opacity: 0.4,
        }} />
      </div>

      {/* Main header content */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
      }}>
        {/* Left: Title with icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Crossed swords / shield icon */}
          <div style={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb, 100, 100, 100), 0.2) 0%, transparent 100%)',
            borderRadius: '4px',
            border: '1px solid var(--color-primary)',
          }}>
            <Icon
              name={isInCombat ? 'sword' : 'target'}
              size={14}
              color="var(--color-primary)"
            />
          </div>

          {/* Title */}
          <span style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'var(--color-text)',
          }}>
            Initiative
          </span>

          {/* Round indicator - prominent when in combat */}
          {isInCombat && (
            <span style={{
              fontFamily: 'var(--font-display)',
              fontSize: '12px',
              fontWeight: 600,
              color: '#ffffff',
              letterSpacing: '0.05em',
            }}>
              Rnd {round}
            </span>
          )}
        </div>

        {/* Right: Controls */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {/* Dock button (only when floating) */}
          {isFloating && (
            <button
              onClick={(e) => { e.stopPropagation(); onDock(); }}
              style={{
                padding: 4,
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Dock panel"
            >
              <Icon name="chevron-left" size={12} />
            </button>
          )}

          {/* Expand/collapse buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
            style={{
              padding: 4,
              backgroundColor: 'transparent',
              border: 'none',
              color: isExpanded ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <Icon name={isExpanded ? 'minus' : 'plus'} size={12} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
            style={{
              padding: 4,
              backgroundColor: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            <Icon name={isCollapsed ? 'chevron-down' : 'chevron-up'} size={12} />
          </button>
        </div>
      </div>

      {/* Drag hint */}
      {!isFloating && (
        <div style={{
          position: 'absolute',
          bottom: -8,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '8px',
          color: 'var(--color-text-muted)',
          opacity: 0.5,
          whiteSpace: 'nowrap',
        }}>
          drag to detach
        </div>
      )}
    </div>
  );
}


interface InitiativeRowProps {
  entry: InitiativeEntry;
  isCurrentTurn: boolean;
  isDM: boolean;
  onRoll: () => void;
  onRemove: () => void;
  onSetInit: (value: number) => void;
  onSelect: () => void;
}

function InitiativeRow({ entry, isCurrentTurn, isDM, onRoll, onRemove, onSetInit, onSelect }: InitiativeRowProps) {
  const characters = useCharacterStore((s) => s.characters);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  // Get character badge info if this is a character entry
  const character = entry.characterId ? characters.get(entry.characterId) : null;
  const badge = character?.badgeId ? getBadgeById(character.badgeId) : null;

  const handleEdit = () => {
    setEditValue(entry.initiative.toString());
    setIsEditing(true);
  };

  const handleSubmit = () => {
    const val = parseInt(editValue, 10);
    if (!isNaN(val)) {
      onSetInit(val);
    }
    setIsEditing(false);
  };

  const getEntryIcon = (): IconName => {
    if (entry.type === 'character') {
      return (entry.badgeIcon as IconName) || 'token';
    }
    return 'skull';
  };

  const bgColor = isCurrentTurn
    ? 'var(--color-primary-subtle)'
    : entry.hasActed
    ? 'rgba(0,0,0,0.2)'
    : 'transparent';

  return (
    <div
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-xs)',
        padding: '4px 8px',
        backgroundColor: bgColor,
        borderLeft: isCurrentTurn ? '3px solid var(--color-primary)' : '3px solid transparent',
        borderRadius: 'var(--radius-sm)',
        opacity: entry.hasActed && !isCurrentTurn ? 0.5 : 1,
        cursor: 'pointer',
      }}
    >
      {/* Initiative value / roll button */}
      {entry.initiative === 0 ? (
        <DiceButton
          onClick={onRoll}
          title={`Roll d20+${entry.dexMod >= 0 ? '+' : ''}${entry.dexMod}`}
          size={28}
        />
      ) : isEditing ? (
        <input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSubmit}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          autoFocus
          style={{
            width: 36,
            padding: '2px 4px',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-primary)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-text)',
            fontSize: 'var(--font-size-sm)',
            textAlign: 'center',
          }}
        />
      ) : (
        <button
          onClick={handleEdit}
          style={{
            minWidth: 28,
            padding: '2px 4px',
            backgroundColor: entry.type === 'character'
              ? (badge?.color || 'var(--color-primary)')
              : 'var(--color-danger)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            color: entry.type === 'character'
              ? (badge?.text || 'white')
              : 'white',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
          title="Click to edit"
        >
          {entry.initiative}
        </button>
      )}

      {/* Icon */}
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: entry.type === 'character'
            ? (badge?.color || 'var(--color-primary-subtle)')
            : 'var(--color-danger-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon
          name={getEntryIcon()}
          size={12}
          color={entry.type === 'character'
            ? (badge?.text || 'var(--color-primary)')
            : 'var(--color-danger)'}
        />
      </div>

      {/* Name */}
      <span
        style={{
          flex: 1,
          fontSize: 'var(--font-size-sm)',
          fontWeight: isCurrentTurn ? 600 : 400,
          color: 'var(--color-text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {character?.name || entry.name}
      </span>

      {/* DEX mod indicator */}
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-tertiary)',
        }}
        title="DEX modifier"
      >
        {entry.dexMod >= 0 ? '+' : ''}{entry.dexMod}
      </span>

      {/* Remove - DM only */}
      {isDM && (
        <button
          onClick={onRemove}
          style={{
            padding: 2,
            backgroundColor: 'transparent',
            border: 'none',
            color: 'var(--color-text-tertiary)',
            cursor: 'pointer',
            opacity: 0.4,
          }}
          title="Remove"
        >
          <Icon name="minus" size={10} />
        </button>
      )}
    </div>
  );
}

// Default floating position (top-right area)
const DEFAULT_FLOAT_POSITION = { x: window.innerWidth - 320, y: 80 };
const DRAG_THRESHOLD = 30; // Pixels to drag before detaching

export function InitiativePanel() {
  const entries = useInitiativeStore((s) => s.entries);
  const currentIndex = useInitiativeStore((s) => s.currentIndex);
  const round = useInitiativeStore((s) => s.round);
  const isInCombat = useInitiativeStore((s) => s.isInCombat);
  const addEntry = useInitiativeStore((s) => s.addEntry);
  const removeEntry = useInitiativeStore((s) => s.removeEntry);
  const setInitiative = useInitiativeStore((s) => s.setInitiative);
  const startCombat = useInitiativeStore((s) => s.startCombat);
  const endCombat = useInitiativeStore((s) => s.endCombat);
  const nextTurn = useInitiativeStore((s) => s.nextTurn);
  const previousTurn = useInitiativeStore((s) => s.previousTurn);
  const resetInitiative = useInitiativeStore((s) => s.resetInitiative);

  const characters = useCharacterStore((s) => s.characters);
  const role = useSessionStore((s) => s.role);
  const isDM = role === 'dm';
  const tokens = useTokenStore((s) => s.tokens);
  const selectToken = useTokenStore((s) => s.selectToken);
  const diceRoll = useDiceStore((s) => s.roll);

  // Select the token on the map that corresponds to this entry
  const selectEntryToken = useCallback((entry: InitiativeEntry) => {
    // Find token by combatantId matching the entry's id (mob) or characterId (character)
    const targetId = entry.type === 'character' ? entry.characterId : entry.id;
    if (!targetId) return;

    for (const token of tokens.values()) {
      if (token.combatantId === targetId) {
        selectToken(token.id);
        return;
      }
    }
  }, [tokens, selectToken]);
  const isDiceReady = useDiceStore((s) => s.isReady);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);  // Start expanded

  // Floating/dragging state
  const [isFloating, setIsFloating] = useState(false);
  const [floatPosition, setFloatPosition] = useState(DEFAULT_FLOAT_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Left click only

    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startX: floatPosition.x,
      startY: floatPosition.y,
    };
    setIsDragging(true);

    // Prevent text selection during drag
    e.preventDefault();
  }, [floatPosition]);

  // Handle mouse move during drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;

      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // If docked and dragged beyond threshold, detach
      if (!isFloating && distance > DRAG_THRESHOLD) {
        const newX = e.clientX - 150; // Center on cursor
        const newY = e.clientY - 20;
        setIsFloating(true);
        setFloatPosition({ x: newX, y: newY });
        // Reset drag start to new position so subsequent moves work correctly
        dragStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          startX: newX,
          startY: newY,
        };
        return; // Skip the position update below since we just set it
      }

      // If floating, update position
      if (isFloating) {
        const newX = Math.max(0, Math.min(window.innerWidth - 280, dragStartRef.current.startX + deltaX));
        const newY = Math.max(0, Math.min(window.innerHeight - 100, dragStartRef.current.startY + deltaY));
        setFloatPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isFloating]);

  // Dock the panel back
  const handleDock = useCallback(() => {
    setIsFloating(false);
    setFloatPosition(DEFAULT_FLOAT_POSITION);
  }, []);

  // Auto-populate party members into initiative — adds any missing characters
  const partyIds = useCharacterStore((s) => s.partyIds);
  useEffect(() => {
    if (partyIds.length === 0 || isInCombat) return;

    for (const pid of partyIds) {
      const char = characters.get(pid);
      if (!char) continue;
      // Skip absent players and system-controlled NPCs
      if (char.absentThisSession) continue;
      if (char.isNpc && char.npcControlMode === 'ai') continue;

      const currentEntries = useInitiativeStore.getState().entries;
      const alreadyExists = currentEntries.some(e => e.characterId === char.id);
      if (alreadyExists) continue;

      const dexMod = getModifier(char.abilityScores.dexterity);
      addEntry({
        id: `char-${char.id}`,
        name: char.name,
        initiative: 0,
        dexMod,
        type: 'character',
        characterId: char.id,
        badgeId: char.badgeId,
        badgeIcon: char.badgeIcon,
        conditions: [],
      });
    }
  }, [partyIds, characters, isInCombat, addEntry]);

  // Roll initiative for a specific entry (uses 3D dice popup)
  // Bridges to combatStore so initiative syncs to Supabase for all players
  const handleRoll = async (entry: InitiativeEntry) => {
    console.log('[Initiative] Rolling for:', entry.name, 'isDiceReady:', isDiceReady);
    const dexModStr = entry.dexMod >= 0 ? `+${entry.dexMod}` : `${entry.dexMod}`;

    const commitInit = (total: number) => {
      // Write to local UI store
      setInitiative(entry.id, total);
      // Bridge to combat store (Supabase-synced) using the characterId or mob id
      const combatId = entry.characterId || entry.id;
      useCombatStore.getState().setInitiative(combatId, total, entry.dexMod);
    };

    let rolled = false;

    if (isDiceReady) {
      try {
        const result = await diceRoll('1d20');
        console.log('[Initiative] 3D dice result:', result);
        if (result) {
          const total = result.total + entry.dexMod;
          commitInit(total);

          // Post to chat
          addDiceRoll({
            sender: entry.name,
            characterName: entry.name,
            notation: `1d20${dexModStr}`,
            result: {
              ...result,
              notation: `1d20${dexModStr}`,
              total,
            },
            rollType: 'normal',
            rollLabel: 'initiative',
          });
          rolled = true;
        }
      } catch (err) {
        console.error('[Initiative] 3D dice error:', err);
      }
    }

    // Fallback if 3D dice not ready OR if they failed/returned null
    if (!rolled) {
      console.log('[Initiative] Using fallback roll');
      const d20 = Math.floor(Math.random() * 20) + 1;
      const total = d20 + entry.dexMod;
      commitInit(total);

      // Post fallback roll to chat
      addDiceRoll({
        sender: entry.name,
        characterName: entry.name,
        notation: `1d20${dexModStr}`,
        result: {
          notation: `1d20${dexModStr}`,
          rolls: [{ sides: 20, value: d20 }],
          total,
        },
        rollType: 'normal',
        rollLabel: 'initiative',
      });
    }
  };

  // Check if all entries have rolled
  const allRolled = entries.length > 0 && entries.every((e) => e.initiative > 0);

  // Panel content (shared between docked and floating)
  const panelContent = (
    <>
      {/* Ornate Header */}
      <OrnateHeader
        isInCombat={isInCombat}
        round={round}
        isFloating={isFloating}
        onStartDrag={handleDragStart}
        onDock={handleDock}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />

      {!isCollapsed && (
        <>
          {/* Quick actions bar removed — party auto-populates from character store */}

          {/* Entry list */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 4 }}>
            {entries.length === 0 ? (
              <div
                style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: 'var(--color-text-tertiary)',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                No characters in party
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {entries.map((entry, index) => (
                  <InitiativeRow
                    key={entry.id}
                    entry={entry}
                    isCurrentTurn={isInCombat && index === currentIndex}
                    isDM={isDM}
                    onRoll={() => handleRoll(entry)}
                    onRemove={() => removeEntry(entry.id)}
                    onSetInit={(val) => setInitiative(entry.id, val)}
                    onSelect={() => selectEntryToken(entry)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Add Monsters button removed — Start Combat auto-adds every alive
              mob on the board. If you need a mob on-board but NOT in init
              (surprise, decoration), we'll add a token-level flag. */}

          {/* Combat controls - DM only */}
          {entries.length > 0 && isDM && (
            <div
              style={{
                display: 'flex',
                gap: 4,
                padding: '6px 8px',
                borderTop: '1px solid var(--color-border)',
                flexShrink: 0,
              }}
            >
              {!isInCombat ? (
                <>
                  <button
                    onClick={resetInitiative}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--color-text-tertiary)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-xs)',
                    }}
                    title="Reset initiative rolls"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => useCombatStore.getState().resyncToDb()}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(212, 175, 55, 0.4)',
                      borderRadius: 'var(--radius-sm)',
                      color: '#d4af37',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-xs)',
                    }}
                    title="Persist current init + round to DB without resetting (recovery for paused fights)"
                  >
                    Resync
                  </button>
                  <button
                    onClick={() => {
                      startCombat();
                      useCombatStore.getState().startCombat();
                    }}
                    disabled={!allRolled}
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      backgroundColor: allRolled ? 'var(--color-success)' : 'var(--color-surface)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      color: allRolled ? 'white' : 'var(--color-text-tertiary)',
                      cursor: allRolled ? 'pointer' : 'default',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                    }}
                    title={allRolled ? 'Start combat' : 'All entries must roll first'}
                  >
                    <Icon name="play" size={10} />
                    Start Combat
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      previousTurn();
                      useCombatStore.getState().prevTurn();
                    }}
                    style={{
                      padding: '4px 6px',
                      backgroundColor: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--color-text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="chevron-left" size={12} />
                  </button>
                  <button
                    onClick={() => {
                      nextTurn();
                      useCombatStore.getState().nextTurn();
                    }}
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      backgroundColor: 'var(--color-primary)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                    }}
                  >
                    Next
                    <Icon name="chevron-right" size={10} />
                  </button>
                  <button
                    onClick={() => {
                      endCombat();
                      useCombatStore.getState().endCombat();
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'var(--color-danger-subtle)',
                      border: '1px solid var(--color-danger)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--color-danger)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    End
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  // Floating panel wrapper
  if (isFloating) {
    return (
      <div
        ref={panelRef}
        data-tour="initiative"
        style={{
          position: 'fixed',
          left: floatPosition.x,
          top: floatPosition.y,
          width: 280,
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          height: isExpanded ? '500px' : isCollapsed ? 'auto' : '340px',
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.1)',
          overflow: 'hidden',
          transition: isDragging ? 'none' : 'height 0.2s ease',
        }}
      >
        {panelContent}
      </div>
    );
  }

  // Docked panel (original location)
  return (
    <div
      ref={panelRef}
      data-tour="initiative"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: isExpanded ? '400px' : isCollapsed ? 'auto' : '220px',
        backgroundColor: 'var(--color-background)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        transition: 'height 0.2s ease',
      }}
    >
      {panelContent}
    </div>
  );
}
